/* eslint-disable max-classes-per-file */

import { PhoneCountryCodeEnum } from '@utils';

export * from './mutations';
export * from './queries';

export enum ClientRole {
  Admin = 'Admin',
  Manager = 'Manager',
  Basic = 'Basic',
  Spouse = 'Spouse',
}

export enum SuperAdminRole {
  Admin = 'Admin',
  Manager = 'Manager',
}

export enum UserType {
  SuperAdmin = 'SuperAdmin',
  Advisor = 'Advisor',
  Client = 'Client',
}

export enum InvitationStatus {
  Pending = 'Pending',
  Accepted = 'Accepted',
  Sent = 'Sent',
}

export enum OnboardingStatus {
  Pending = 'Pending',
  Completed = 'Completed',
}
export class AdvisorInput {
  email: string;

  firstName: string;

  lastName: string;

  phone: string;

  title?: Nullable<string>;

  company: string;

  linkedinUrl?: Nullable<string>;

  bookingUrl?: Nullable<string>;

  bio?: Nullable<string>;

  password: string;
}

export class ManagerInput {
  email: string;

  name: string;
}

export class ClientInput {
  email: string;

  firstName: string;

  org: string;

  phone?: Nullable<string>;

  lastName: string;

  role?: Nullable<ClientRole>;
}

export enum ClientLanguage {
  English = 'En',
  French = 'Fr',
}

export enum AdvisorLanguage {
  English = 'En',
  French = 'Fr',
}

export class UpdateClientInput {
  id: string;

  firstName: string;

  lastName: string;

  phone?: Nullable<string>;

  role?: Nullable<ClientRole>;

  email: string;

  workEmail: string;

  preferredLanguage?: Nullable<ClientLanguage>;
}

export class ClientOrganizationInput {
  name: string;

  email?: string;

  firstName?: string;

  lastName?: string;

  status?: Nullable<OrganizationStatus>;

  message?: Nullable<string>;

  managerId?: Nullable<string>;

  advisorId?: Nullable<string>;

  preferredLanguage?: Nullable<ClientLanguage>;

  isTcrOnly?: Nullable<boolean>;

  isTcrActive?: Nullable<boolean>;

  isPayingCustomer?: Nullable<boolean>;
}

export class UpdateClientOrganizationInput {
  id: string;

  name: string;

  status: OrganizationStatus;

  advisorId?: Nullable<string>;

  isPayingCustomer?: Nullable<boolean>;

  currentTierId?: Nullable<string>;
}

export enum OrganizationStatus {
  Created = 'Created',
  Active = 'Active',
  Inactive = 'Inactive',
}

export class Advisor {
  id: string;

  email: string;

  firstName: string;

  lastName: string;

  phone: string;

  title?: Nullable<string>;

  company: string;

  linkedinUrl?: Nullable<string>;

  bookingUrl?: Nullable<string>;

  bio?: Nullable<string>;

  preferredLanguage?: Nullable<AdvisorLanguage>;
}

export class Client {
  id: string;

  email: string;

  workEmail: string;

  firstName: string;

  lastName: string;

  phone?: Nullable<string>;

  role: ClientRole;

  advisor?: Nullable<Advisor>;

  onboardingStatus: OnboardingStatus;

  invitationStatus: InvitationStatus;

  preferredLanguage: ClientLanguage;
}

export class OrganizationClientsFilter {
  search?: Nullable<string>;

  role?: Nullable<ClientRole>;

  invitationStatus?: Nullable<InvitationStatus>;

  onboardingStatus?: Nullable<OnboardingStatus>;
}

export class OrganizationClientsPage {
  items: Client[];

  totalCount: number;
}

export class ClientOrganization {
  id: string;

  name: string;

  status: OrganizationStatus;

  admin: SuperAdmin;

  advisor: Advisor;

  allClients?: Client[];

  /** Number of clients in the organization, counted on the server. */
  clientsCount?: number;

  /**
   * First client with the Admin role. Not a schema field: the organizations store fills it from
   * the organization's paginated clients query.
   */
  adminClient?: Nullable<Pick<Client, 'id' | 'email'>>;

  manageOrgToken?: string;

  orgLogs: OrganizationLog[];

  isTcrOnly?: Nullable<boolean>;

  currentTier: Tier;

  organizationFeatures: OrganizationFeature[];

  organizationBilling: OrganizationBilling[];

  isPayingCustomer?: Nullable<boolean>;

  createdAt: string;

  logoUrl: Nullable<string>;
}

export class OrganizationFeature {
  id: string;

  feature: Feature;

  type: string;

  enabledAt: string;

  disabledAt: string;
}

export class OrganizationFeatureLimitsJsonInput {
  customer_support?: Nullable<string>;

  sessions_per_user_per_year?: Nullable<number>;

  bank_institution_connections?: Nullable<number>;
}

export class OrganizationFeatureInput {
  id?: string;

  organizationId?: string;

  featureId?: string;

  type?: string;

  limitsJson?: OrganizationFeatureLimitsJsonInput;

  enabledAt?: Nullable<string>;

  disabledAt?: Nullable<string>;
}

export class OrganizationLog {
  operationName: string;

  actionLabel?: Nullable<string>;

  superadmin: Nullable<SuperAdmin>;

  createdAt: string;
}

export class Session {
  user: User;

  auth: SessionAuth;
}

export class SessionAuth {
  token: string;
}

export class SuperAdmin {
  id: string;

  name: string;

  role: SuperAdminRole;

  email: string;

  adminRole: SuperAdminRole;

  phone: string;
}

export class AssignManagerInput {
  name: string;

  orgId: string;

  managerId: string;
}

export class AssignAdvisorToClientInput {
  advisorId: string;

  clientId: string;
}

export class DeleteClientInput {
  orgId: string;

  clients: string[];
}

export class AdvisorRequest {
  id: string;

  client: Client;
}

export class SettingsEmergencySavings {
  interestRate: number;
}

export class SettingsInsurance {
  disability: SettingsInsuranceDisability;

  life: SettingsInsuranceLife;
}

export class SettingsInsuranceDisability {
  incomeCoveragePercent: number;
}

export class SettingsInsuranceLife {
  inflationRate: number;

  incomePercentToReplace: number;

  canadaPensionPlanPayout: number;

  funeralExpenses: number;

  survivorBenefitsUnder65: number;

  survivorBenefitsAbove65: number;

  childrensBenefits: number;
}

export class SettingsDebt {
  refinancingMortgage: SettingsDebtInterest;

  refinancingByAllDebts: SettingsDebtInterest;

  refinancingWithoutMortgage: SettingsDebtInterest;

  mortgageQualification: SettingsDebtMortgageQualification;
}

export class SettingsDebtInterest {
  interestRate: number;

  totalYearsToPayDebt: number;
}

export class SettingsDebtMortgageQualification {
  interestRate: number;

  totalYearsToPayDebt: number;

  downPayment: number;

  propertyInsurance?: Nullable<number>;

  grossDebtServiceRatio?: Nullable<number>;

  totalDebtServiceRatio: number;
}

export class SettingsRiskTolerance {
  growth: number;

  moderateGrowth: number;

  moderate: number;

  conservativeGrowth: number;

  conservative: number;
}

export class Settings {
  tax: SettingsTax;

  emergencySavings: SettingsEmergencySavings;

  insurance: SettingsInsurance;

  expenses: SettingsExpenses;

  debt: SettingsDebt;

  retirement: SettingsRetirement;

  riskTolerance: SettingsRiskTolerance;
}

export class SettingsRetirement {
  oasStartingAge: number;

  appreciationRate: number;

  inflationRate: number;

  incomeRequiredAtRetirement: number;

  definedContributionMaxLimit: number;

  groupRrspContributionMaxLimit: number;

  rrspContributionMaxLimit: number;

  tfsaContributionMaxLimit: number;

  maxCppPayout: number;

  maxOasPayout: number;
}

export class SettingsExpenses {
  thresholds: SettingsExpensesThresholds;
}

export class SettingsExpensesThresholds {
  transportationTotal: number;

  recreationTotal: number;

  housingTotal: number;

  housingUtilities: number;

  personalCareClothing: number;

  personalCareMedical: number;

  personalCareGroceries: number;
}

export class SettingsTax {
  federal: TaxSlab[];

  ab: TaxSlab[];

  bc: TaxSlab[];

  mb: TaxSlab[];

  nb: TaxSlab[];

  nl: TaxSlab[];

  ns: TaxSlab[];

  nt: TaxSlab[];

  nu: TaxSlab[];

  on: TaxSlab[];

  pe: TaxSlab[];

  qc: TaxSlab[];

  sk: TaxSlab[];

  yt: TaxSlab[];

  cppIncomeThreshold: number;

  cppCredit: TaxCredit;

  eiCredit: TaxCredit;

  personalCredit: number;

  refundableTaxClaim: number;

  canadaEmploymentAmount: number;
}

export class TaxSlab {
  limit: number;

  rate: number;
}

export class TaxCredit {
  contributionRate: number;

  threshold: number;
}

export type User = SuperAdmin | Advisor | Client;

type Nullable<T> = T | null;

export class DeleteOrganizationInput {
  id: string;
}

export class DeleteAdvisorInput {
  id: string;
}

export class UpdateAdvisorInput {
  id: string;

  firstName: string;

  lastName: string;

  phone: string;

  title?: Nullable<string>;

  company: string;

  preferredLanguage?: Nullable<AdvisorLanguage>;

  bookingUrl?: Nullable<string>;
}

export class UpdateManagerInput {
  id: string;

  name: string;

  role: SuperAdminRole;
}

export class VerifyPhoneChangeInput {
  code: string; 

  phone: string;

  phoneCountryCode: PhoneCountryCodeEnum;
}

export class OrgIdsByFeatureFlagConfig {
  featureFlagName: string;

  orgIds: string[];
}

export class Tier {
  id: string;

  name: string;

  code: string;

  description: string;

  entitlements: Entitlement[];

  stripeProductId: string;

  priceMonthly: number;

  priceYearly: number;
}

export enum EntitlementType {
  FEATURE = 'feature',
  ADD_ON = 'add-on',
}

export class Entitlement {
  id: string;

  isIncluded: boolean;

  type: EntitlementType;

  feature: Feature;

  limitsJson: LimitsJson;
}

export class LimitsJson {
  customer_support: string;

  sessions_per_user_per_year: number;

  bank_institution_connections: number;
}
export class Feature {
  id: string;

  name: string;

  featureFlagKey: string;

  description: string;
}

export enum RefundStatus {
  Pending = 'pending',
  Paid = 'paid',
  Failed = 'failed',
  Refunded = 'refunded',
}

export class PaymentMethod {
  last4Digits: string;

  institution: string;
}

export enum InvoiceStatus {
  draft = 'draft',
  open = 'open',
  paid = 'paid',
  uncollectible = 'uncollectible',
  void = 'void',
}

export class InvoiceLine {
  id: string;

  description: string;

  quantity: number;

  product: string;
}

export class Invoice {
  id: string;

  number: string;

  status: InvoiceStatus;

  amountPaid: number;

  amountDue: number;

  amountRemaining: number;

  currency: string;

  description: string;

  invoicePdf: string;

  hostedInvoiceUrl: string;

  editInvoiceUrl: string;

  periodStart: string;

  periodEnd: string;

  dueDate: string;

  createdAt: string;

  paidAt: string;

  subscriptionId: string;

  customerId: string;

  attempted: boolean;

  attemptCount: number;

  lines: InvoiceLine[];

  paymentIntentId: string;
}

export enum CreditReason {
  ServiceInterruption = 'serviceInterruption',
  BillingError = 'billingError',
  CustomerSupportResolution = 'customerSupportResolution',
  PromotionalCredit = 'promotionalCredit',
  GoodwillGesture = 'goodwillGesture',
  Other = 'other',
}

export enum RefundType {
  Full = 'full',
  Partial = 'partial',
}

export enum RefundReason {
  RequestedByCustomer = 'requestedByCustomer',
  ServiceNotDelivered = 'serviceNotDelivered',
  BillingError = 'billingError',
  TechnicalIssue = 'technicalIssue',
  FraudulentTransaction = 'fraudulentTransaction',
  DuplicateCharge = 'duplicateCharge',
  Other = 'other',
}

export enum BillingInterval {
  monthly = 'monthly',
  yearly = 'yearly',
}

export enum SubscriptionStatus {
  Pending = 'Pending',
  Trialing = 'Trialing',
  Active = 'Active',
  PastDue = 'PastDue',
  Canceled = 'Canceled',
  CancelingAtPeriodEnd = 'CancelingAtPeriodEnd',
  Downgrading = 'Downgrading',
}

export enum SubscriptionInterval {
  monthly = 'month',
  yearly = 'year',
}

export class PaymentMethodDetails {
  id: string;

  type: string;

  last4: string;

  brand: string;

  expMonth: number;

  expYear: number;
}

export class SubscriptionDetailsBreakdown {
  id: string;

  amount: number;

  name: string;

  type: ProductType;
}

export class SubscriptionDetails {
  amount: number;

  interval: BillingInterval;

  status: string;

  breakdown: SubscriptionDetailsBreakdown[];
}

export enum BillingModel {
  CREDIT_CARD = 'CREDIT_CARD',
  INVOICE = 'INVOICE',
}
export class OrganizationBilling {
  id: string;

  provider: string;

  status: SubscriptionStatus;

  purchasedSeats: number;

  startedAt: string;

  endsAt: string;

  usedSeats: number;

  billingInterval: BillingInterval;

  billingModel: BillingModel;

  purchasedTier: Tier;

  addons: Feature[];

  paymentMethod: PaymentMethodDetails;

  subscriptionDetails: SubscriptionDetails;

  discount?: Nullable<DiscountInfo>;

  trialEndsAt: string;

  nextBillingDate?: Nullable<string>;

  trialPeriodDays?: Nullable<number>;
}

export class PaymentIntentError {
  code: string;

  docUrl: string;

  type: string;

  declineCode: string;

  message: string;
}

export class PaymentIntentPaymentMethod {
  id: string;

  type: string;

  last4: string;

  brand: string;

  expMonth: number;

  expYear: number;

  funding: string;
}

export class PaymentIntent {
  id: string;

  status: string;

  created: string;

  currency: string;

  amount: number;

  paymentMethod: PaymentIntentPaymentMethod;

  error: PaymentIntentError;
}

export enum OrganizationTierCategory {
  Core = 'core',
  Pro = 'pro',
  Enterprise = 'enterprise',
}

export enum TierCodes {
  CORE_V1 = 'core_v1',
  PRO_V1 = 'pro_v1',
  ENTERPRISE_V1 = 'enterprise_v1',
}

export enum CreateSubscriptionStatus {
  active = 'active',
  incomplete = 'incomplete',
}

export class DiscountCode {
  id: string;

  name: string;

  code: string;

  discountPercentage: number;

  maxRedemptions: number;

  timesRedeemed: number;

  active: boolean;
}

export class CreateDiscountCodeInput {
  discountPercentage: number;

  maxRedemptions?: number;
}

export class CreateDiscountCodeResponse {
  success: boolean;

  discountCode: DiscountCode;
}

export class DeleteDiscountCodeResponse {
  success: boolean;

  message: string;
}

export class DiscountInfo {
  promotionCodeId: string;

  couponId: string;

  code: string;

  couponName: string;

  percentOff: number;

  amountOff: number;
}

export class ApplyDiscountCodeInput {
  promotionCode: string;

  orgId: string;
}

export class RemoveDiscountCodeInput {
  orgId: string;
}

export class ApplyDiscountCodeResponse {
  success: boolean;

  message: string;

  promotionCodeId: string;

  couponId: string;

  code: string;
}

export class RemoveDiscountCodeResponse {
  success: boolean;

  message: string;
}

export class MarkInvoiceAsPaidInput {
  invoiceId: string;
}

export class MarkInvoiceAsPaidResponse {
  success: boolean;

  invoiceId: string;

  invoiceStatus: string;

  message: string;
}

export enum ProductType {
  TIER = 'TIER',
  FEATURE = 'FEATURE',
}

export class CustomRateReference {
  id: string;

  stripePriceId?: string;

  productType?: ProductType;

  tierId?: string;

  featureId?: string;

  amount?: number;
}

export class SaveCustomPriceInput {
  id: string | undefined;

  tierId: string | undefined;

  featureId: string | undefined;

  amount: number;
}

export class SaveCustomPriceResult {
  id: string | undefined;

  operation: string | undefined;

  success: boolean;

  message: string | undefined;
}

export class UpdateOrganizationBillingInput {
  tierId?: Nullable<string>;

  purchasedSeats?: Nullable<number>;

  billingInterval?: Nullable<BillingInterval>;

  billingModel?: Nullable<BillingModel>;

  startsAt?: Nullable<string>;

  endsAt?: Nullable<string>;

  addons?: Nullable<string[]>;

  trialPeriodDays?: Nullable<number>;
}

export class SaveOrganizationBillingDetailsResponse {
  success: boolean;

  message: string;
}

export class CreateStripeCustomerResponse {
  customerId: string;

  created: boolean;
}

export type ActivityLogSortField = 'createdAt' | 'administrator' | 'organization' | 'action' | 'target';

export interface ActivityLogFilterInput {
  orgIds: string[] | null;
  includeNoOrganization: boolean;
  superadminIds: string[] | null;
  includeSystem: boolean;
  actions: string[] | null;
  from: string | null;
  to: string | null;
  search: string | null;
}

export interface ActivityLogAdministrator {
  id: string;
  name?: Nullable<string>;
  email?: Nullable<string>;
  deleted: boolean;
}

export interface ActivityLogOrganization {
  id: string;
  name?: Nullable<string>;
  deleted: boolean;
}

export interface ActivityLogTarget {
  type: string;
  id?: Nullable<string>;
  label?: Nullable<string>;
}

export interface ActivityLogEntry {
  id: string;
  createdAt: string;
  administrator?: Nullable<ActivityLogAdministrator>;
  organization?: Nullable<ActivityLogOrganization>;
  action: string;
  actionLabel?: Nullable<string>;
  target?: Nullable<ActivityLogTarget>;
}

export interface PaginatedActivityLog {
  items: ActivityLogEntry[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ActivityLogActionOption {
  name: string;
  label?: Nullable<string>;
}

export interface ActivityLogFilterOptions {
  organizations: ActivityLogOrganization[];
  administrators: ActivityLogAdministrator[];
  actions: ActivityLogActionOption[];
}

export interface ActivityLogExportRow {
  timestampUtc: string;
  administratorName?: Nullable<string>;
  administratorEmail?: Nullable<string>;
  organizationId?: Nullable<string>;
  organizationName?: Nullable<string>;
  action: string;
  actionLabel?: Nullable<string>;
  targetType?: Nullable<string>;
  targetId?: Nullable<string>;
  targetLabel?: Nullable<string>;
}

export interface ActivityLogExport {
  rows: ActivityLogExportRow[];
  total: number;
}
